import {
	type FieldPathByValue,
	type FieldValues,
	type UseControllerProps,
	useController,
} from "react-hook-form";
import { EMPTY_BOND_FORM } from "../../../constants";
import type { BondFormData } from "../../../types";
import { FormFieldError } from "../FormFieldError";
import { BondFormSelectInput, type BondFormSelectInputProps } from "../inputs/BondFormSelectInput";

export type FormBondFormSelectProps<
	TFieldValues extends FieldValues,
	TName extends FieldPathByValue<TFieldValues, BondFormData | undefined> = FieldPathByValue<
		TFieldValues,
		BondFormData | undefined
	>,
	TTransformedValues = TFieldValues,
> = UseControllerProps<TFieldValues, TName, TTransformedValues> &
	Omit<BondFormSelectInputProps, "value" | "onChange" | "onBlur" | "error"> & {
		errorMessage?: string;
	};

export const FormBondFormSelect = <
	TFieldValues extends FieldValues,
	TName extends FieldPathByValue<TFieldValues, BondFormData | undefined> = FieldPathByValue<
		TFieldValues,
		BondFormData | undefined
	>,
	TTransformedValues = TFieldValues,
>({
	name,
	control,
	defaultValue,
	errorMessage,
	...inputProps
}: FormBondFormSelectProps<TFieldValues, TName, TTransformedValues>) => {
	const {
		field: { value, onChange, onBlur },
		fieldState: { error },
	} = useController<TFieldValues, TName, TTransformedValues>({ name, control, defaultValue });

	return (
		<div className="relative">
			<BondFormSelectInput
				{...inputProps}
				value={value ?? EMPTY_BOND_FORM}
				onChange={onChange}
				onBlur={onBlur}
				error={!!error}
			/>
			{error && <FormFieldError error={error} errorMessage={errorMessage} />}
		</div>
	);
};
